import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

const { Title, Text } = Typography;

import type { AdminUserSummary } from '../types';

type AdminUserAccessFilter = 'all' | 'with-current-access' | 'without-current-access';

type CreateUserValues = {
  email: string;
  name?: string;
  password: string;
  skipVerificationEmail?: boolean;
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [ignoreAnnea, setIgnoreAnnea] = useState(true);
  const [accessFilter, setAccessFilter] = useState<AdminUserAccessFilter>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<CreateUserValues>();

  useEffect(() => {
    loadUsers();
  }, [search, ignoreAnnea, accessFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await api.admin.listUsers({
        search,
        ignoreAnnea,
        access: accessFilter,
      });
      setUsers(data.users || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (values: CreateUserValues) => {
    try {
      setCreating(true);
      await api.admin.createUser(values);
      message.success(
        values.skipVerificationEmail
          ? 'Verified user created without a confirmation email.'
          : 'User created. Verification email sent.'
      );
      setModalOpen(false);
      form.resetFields();
      await loadUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setIgnoreAnnea(true);
    setAccessFilter('all');
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setLoading(true);
      await api.admin.deleteUser(userId);
      message.success('User deleted');
      await loadUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<AdminUserSummary> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (value, record) => (
        <div>
          <Text strong>{record.name || value}</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            {value}
          </Text>
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      title: 'Organization',
      key: 'organization',
      render: (_value, record) => {
        const orgName = record.organizationMembers?.[0]?.organization?.name;
        return orgName || (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No organization
          </Text>
        );
      },
    },
    {
      title: 'Subscriptions',
      dataIndex: '_count',
      key: 'subscriptions',
      render: (_count, record) => {
        const total = record._count?.userSubscriptions ?? 0;
        const personalPaid = record.personalPaidSubscriptionCount ?? 0;
        const personalTrial = record.personalTrialSubscriptionCount ?? 0;
        const organizationPaid = record.organizationPaidSeatCount ?? 0;
        const organizationTrial = record.organizationTrialSeatCount ?? 0;
        const currentAccessTotal =
          personalPaid + personalTrial + organizationPaid + organizationTrial;
        return (
          <div>
            {currentAccessTotal > 0 ? (
              <Space size={4} wrap>
                {personalPaid > 0 && <Tag color="green">Personal paid: {personalPaid}</Tag>}
                {personalTrial > 0 && <Tag color="blue">Personal trial: {personalTrial}</Tag>}
                {organizationPaid > 0 && <Tag color="purple">Org seat paid: {organizationPaid}</Tag>}
                {organizationTrial > 0 && <Tag color="cyan">Org seat trial: {organizationTrial}</Tag>}
              </Space>
            ) : (
              <Text type="secondary">No current access</Text>
            )}
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {total} personal subscription records
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => (
        <Space>
          <Button type="link" onClick={() => navigate(`/admin/users/${record.id}`)}>
            Manage
          </Button>
          <Popconfirm
            title="Delete user?"
            description="Only possible when there are no active subscriptions."
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Admin: Users</Title>
          <Text type="secondary">Create and manage user subscriptions</Text>
        </div>
        <Button type="primary" onClick={() => setModalOpen(true)}>
          Create User
        </Button>
      </div>

      <Space size="middle" wrap align="end">
        <div style={{ minWidth: 320 }}>
          <Text type="secondary">Search</Text>
          <Input.Search
            allowClear
            value={searchInput}
            placeholder="Name, email, or organization"
            onChange={(event) => {
              const nextValue = event.target.value;
              setSearchInput(nextValue);
              if (!nextValue) {
                setSearch('');
              }
            }}
            onSearch={(value) => setSearch(value.trim())}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <Text type="secondary">Access</Text>
          <Select<AdminUserAccessFilter>
            style={{ width: '100%' }}
            value={accessFilter}
            onChange={setAccessFilter}
            options={[
              { value: 'all', label: 'All users' },
              { value: 'with-current-access', label: 'Has subscription/trial' },
              { value: 'without-current-access', label: 'No subscription/trial' },
            ]}
          />
        </div>
        <Checkbox
          checked={ignoreAnnea}
          onChange={(event) => setIgnoreAnnea(event.target.checked)}
        >
          Ignore Annea users
        </Checkbox>
        <Button onClick={clearFilters}>Clear Filters</Button>
      </Space>

      <Table
        rowKey="id"
        dataSource={users}
        columns={columns}
        loading={loading}
        pagination={false}
      />

      <Modal
        title="Create User"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={480}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateUser}
          initialValues={{ skipVerificationEmail: false }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input placeholder="Full name (optional)" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password placeholder="Set temporary password" />
          </Form.Item>
          <Form.Item name="skipVerificationEmail" valuePropName="checked">
            <Checkbox>
              Mark email as verified and do not send a confirmation email
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
